import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Users, UserCheck, UserX, Search, MoreVertical, 
  Mail, Phone, Shield, AlertCircle, Store, ShoppingBag,
  Ban, CheckCircle, Edit, Eye, Key
} from "lucide-react";
import { format } from "date-fns";

interface Profile {
  id: string;
  role?: string; // Optional - fetched separately from user_roles table
  username?: string;
  full_name?: string;
  phone?: string;
  whatsapp_number?: string;
  avatar_url?: string;
  business_name?: string;
  business_description?: string;
  business_category?: string;
  verified: boolean;
  rating?: number;
  total_sales?: number;
  product_count?: number;
  subscription_tier?: string;
  subscription_status?: string;
  created_at: string;
  updated_at: string;
}

export function UserAccounts() {
  const { toast } = useToast();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  
  // Edit form states
  const [editRole, setEditRole] = useState("");
  const [editVerified, setEditVerified] = useState(false);
  const [editSubscriptionTier, setEditSubscriptionTier] = useState("");

  // Fetch user email when details dialog opens
  const fetchUserEmail = async (userId: string) => {
    setLoadingEmail(true);
    setUserEmail(null);
    try {
      const { data, error } = await supabase.rpc('admin_get_user_email', {
        p_user_id: userId
      });
      if (!error && data) {
        setUserEmail(data);
      }
    } catch (error) {
      console.error('Error fetching user email:', error);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleViewDetails = (profile: Profile) => {
    setSelectedProfile(profile);
    setDetailsOpen(true);
    fetchUserEmail(profile.id);
  };

  useEffect(() => {
    fetchProfiles();
  }, [filterRole]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      
      // Fetch profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch all user roles
      const { data: rolesData, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) throw rolesError;

      // Create a map of user_id to role
      const roleMap = new Map<string, string>();
      rolesData?.forEach((r) => {
        roleMap.set(r.user_id, r.role);
      });

      // Merge roles into profiles
      const profilesWithRoles = (profilesData || []).map((profile) => ({
        ...profile,
        role: roleMap.get(profile.id) || "buyer",
      }));

      // Apply role filter if set
      let filteredData = profilesWithRoles;
      if (filterRole !== "all") {
        filteredData = profilesWithRoles.filter((p) => p.role === filterRole);
      }

      setProfiles(filteredData);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast({
        title: "Error",
        description: "Failed to load user accounts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!selectedProfile) return;

    try {
      // Update profile fields (not role - role is in user_roles table)
      const profileUpdates: any = {
        verified: editVerified,
        updated_at: new Date().toISOString(),
      };

      if (editSubscriptionTier) {
        profileUpdates.subscription_tier = editSubscriptionTier;
      }

      const { error: profileError } = await supabase
        .from("profiles")
        .update(profileUpdates)
        .eq("id", selectedProfile.id);

      if (profileError) throw profileError;

      // Update role in user_roles table if changed
      if (editRole && editRole !== selectedProfile.role) {
        // Delete old role and insert new one (unique constraint is on user_id, role)
        // First delete the specific old role
        const { error: deleteError } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", selectedProfile.id)
          .eq("role", selectedProfile.role as "buyer" | "seller" | "admin");

        if (deleteError) {
          console.error("Error deleting old role:", deleteError);
          // Continue anyway - user might not have had a role
        }

        // Insert the new role
        const { error: insertError } = await supabase
          .from("user_roles")
          .insert({
            user_id: selectedProfile.id,
            role: editRole as "buyer" | "seller" | "admin",
          });

        if (insertError) {
          console.error("Error inserting new role:", insertError);
          throw insertError;
        }
      }

      toast({
        title: "Profile Updated",
        description: `User profile has been updated successfully`,
      });

      setEditOpen(false);
      fetchProfiles();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update user profile",
        variant: "destructive",
      });
    }
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      // In a real app, you'd update the user's auth status
      toast({
        title: "User Suspended",
        description: "User account has been suspended",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to suspend user",
        variant: "destructive",
      });
    }
  };

  const handleVerifySeller = async (sellerId: string) => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          verified: true,
          updated_at: new Date().toISOString()
        })
        .eq("id", sellerId);

      if (error) throw error;

      toast({
        title: "Seller Verified",
        description: "Seller account has been verified successfully",
      });

      fetchProfiles();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to verify seller",
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async () => {
    if (!selectedProfile) return;

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please ensure both passwords are the same",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    setResettingPassword(true);
    try {
      const response = await supabase.functions.invoke('admin-reset-password', {
        body: {
          userId: selectedProfile.id,
          newPassword: newPassword,
        },
      });

      if (response.error) throw response.error;

      toast({
        title: "Password Reset",
        description: `Password has been reset successfully for ${selectedProfile.full_name || selectedProfile.username || 'this user'}`,
      });

      setResetPasswordOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Error",
        description: "Failed to reset password",
        variant: "destructive",
      });
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredProfiles = profiles.filter(profile => {
    const searchLower = searchQuery.toLowerCase();
    return (
      profile.username?.toLowerCase().includes(searchLower) ||
      profile.full_name?.toLowerCase().includes(searchLower) ||
      profile.business_name?.toLowerCase().includes(searchLower) ||
      profile.phone?.includes(searchQuery) ||
      profile.whatsapp_number?.includes(searchQuery)
    );
  });

  // Calculate stats based on all profiles (not filtered)
  const allProfiles = profiles;
  const pendingApprovalUsers = allProfiles.filter(p => 
    (p.role === "seller" && !p.verified) || // Unverified sellers
    (!p.role || p.role === "buyer") && !p.verified // New users without proper setup
  );
  
  const stats = {
    total: allProfiles.length,
    buyers: allProfiles.filter(p => p.role === "buyer").length,
    sellers: allProfiles.filter(p => p.role === "seller").length,
    admins: allProfiles.filter(p => p.role === "admin").length,
    verified: allProfiles.filter(p => p.role === "seller" && p.verified).length,
    pendingApproval: allProfiles.filter(p => p.role === "seller" && !p.verified).length,
  };

  // Filter profiles for each tab
  const pendingSellers = filteredProfiles.filter(p => p.role === "seller" && !p.verified);
  const verifiedSellers = filteredProfiles.filter(p => p.role === "seller");
  const buyersList = filteredProfiles.filter(p => p.role === "buyer");
  const adminsList = filteredProfiles.filter(p => p.role === "admin");

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">All registered users</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Buyers</CardTitle>
            <ShoppingBag className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.buyers}</div>
            <p className="text-xs text-muted-foreground">Active buyers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Sellers</CardTitle>
            <Store className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.sellers}</div>
            <p className="text-xs text-muted-foreground">Registered sellers</p>
          </CardContent>
        </Card>

        <Card className={stats.pendingApproval > 0 ? "border-orange-300 bg-orange-50/50" : ""}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
            <AlertCircle className={`h-4 w-4 ${stats.pendingApproval > 0 ? "text-orange-600" : "text-muted-foreground"}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingApproval}</div>
            <p className="text-xs text-muted-foreground">Awaiting verification</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Verified</CardTitle>
            <UserCheck className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.verified}</div>
            <p className="text-xs text-muted-foreground">Verified sellers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
            <Shield className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.admins}</div>
            <p className="text-xs text-muted-foreground">System admins</p>
          </CardContent>
        </Card>
      </div>

      {/* Main User Management Card */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>User Account Management</CardTitle>
              <CardDescription>Manage all user accounts and permissions</CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  className="pl-10 w-64"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="buyer">Buyers</SelectItem>
                  <SelectItem value="seller">Sellers</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="space-y-4">
            <TabsList className="w-full overflow-x-auto flex justify-start sm:grid sm:grid-cols-5">
              <TabsTrigger value="all" className="whitespace-nowrap flex-shrink-0">
                All Users ({stats.total})
              </TabsTrigger>
              <TabsTrigger value="pending-sellers" className="whitespace-nowrap flex-shrink-0">
                Pending Approval {stats.pendingApproval > 0 && (
                  <Badge variant="destructive" className="ml-2 text-xs">{stats.pendingApproval}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="sellers" className="whitespace-nowrap flex-shrink-0">Sellers</TabsTrigger>
              <TabsTrigger value="buyers" className="whitespace-nowrap flex-shrink-0">Buyers</TabsTrigger>
              <TabsTrigger value="admins" className="whitespace-nowrap flex-shrink-0">Admins</TabsTrigger>
            </TabsList>

            {/* All Users Tab */}
            <TabsContent value="all">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading users...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProfiles.map((profile) => (
                      <TableRow key={profile.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {profile.full_name || profile.username || "Unnamed User"}
                            </div>
                            {profile.business_name && (
                              <div className="text-sm text-muted-foreground">
                                {profile.business_name}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {profile.phone && (
                              <div className="flex items-center gap-1 text-sm">
                                <Phone className="h-3 w-3" />
                                {profile.phone}
                              </div>
                            )}
                            {profile.whatsapp_number && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                WhatsApp: {profile.whatsapp_number}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={profile.role === "admin" ? "destructive" : "outline"}>
                            {profile.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {profile.role === "seller" && (
                            <Badge variant={profile.verified ? "default" : "secondary"}>
                              {profile.verified ? "Verified" : "Unverified"}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(profile.created_at), "MMM dd, yyyy")}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleViewDetails(profile)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedProfile(profile);
                                  setEditRole(profile.role);
                                  setEditVerified(profile.verified);
                                  setEditSubscriptionTier(profile.subscription_tier || "free");
                                  setEditOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Profile
                              </DropdownMenuItem>
                              {profile.role === "seller" && !profile.verified && (
                                <DropdownMenuItem
                                  onClick={() => handleVerifySeller(profile.id)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Verify Seller
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedProfile(profile);
                                  setNewPassword("");
                                  setConfirmPassword("");
                                  setResetPasswordOpen(true);
                                }}
                              >
                                <Key className="h-4 w-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleSuspendUser(profile.id)}
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Suspend Account
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Pending Sellers Tab */}
            <TabsContent value="pending-sellers">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading pending sellers...</div>
              ) : (
                <div className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Review and approve seller applications. Verified sellers can list products and receive payments.
                    </AlertDescription>
                  </Alert>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Business Info</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Applied</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingSellers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No pending seller approvals
                          </TableCell>
                        </TableRow>
                      ) : pendingSellers.map((profile) => (
                          <TableRow key={profile.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {profile.business_name || "N/A"}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {profile.full_name || profile.username}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {profile.phone && (
                                  <div className="text-sm">{profile.phone}</div>
                                )}
                                {profile.whatsapp_number && (
                                  <div className="text-sm text-muted-foreground">
                                    WhatsApp: {profile.whatsapp_number}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {profile.business_category || "Not specified"}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <p className="text-sm truncate">
                                {profile.business_description || "No description"}
                              </p>
                            </TableCell>
                            <TableCell>
                              {format(new Date(profile.created_at), "MMM dd, yyyy")}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleVerifySeller(profile.id)}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedProfile(profile);
                                    setDetailsOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Review
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Sellers Tab */}
            <TabsContent value="sellers">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading sellers...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Business</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Sales</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {verifiedSellers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No sellers found
                        </TableCell>
                      </TableRow>
                    ) : verifiedSellers.map((profile) => (
                        <TableRow key={profile.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {profile.business_name || "N/A"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {profile.business_category || "Uncategorized"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {profile.full_name || profile.username || "N/A"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={profile.verified ? "default" : "secondary"}>
                              {profile.verified ? "Verified" : "Pending"}
                            </Badge>
                          </TableCell>
                          <TableCell>{profile.product_count || 0}</TableCell>
                          <TableCell>{profile.total_sales || 0}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {profile.subscription_tier || "free"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleViewDetails(profile)}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedProfile(profile);
                                    setEditRole(profile.role);
                                    setEditVerified(profile.verified);
                                    setEditSubscriptionTier(profile.subscription_tier || "free");
                                    setEditOpen(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Profile
                                </DropdownMenuItem>
                                {!profile.verified && (
                                  <DropdownMenuItem
                                    onClick={() => handleVerifySeller(profile.id)}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Verify Seller
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => handleSuspendUser(profile.id)}
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Suspend
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Buyers Tab */}
            <TabsContent value="buyers">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading buyers...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyersList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No buyers found
                        </TableCell>
                      </TableRow>
                    ) : buyersList.map((profile) => (
                        <TableRow key={profile.id}>
                          <TableCell>
                            {profile.full_name || profile.username || "N/A"}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {profile.phone && (
                                <div className="text-sm">{profile.phone}</div>
                              )}
                              {profile.whatsapp_number && (
                                <div className="text-sm text-muted-foreground">
                                  WhatsApp: {profile.whatsapp_number}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>0</TableCell>
                          <TableCell>
                            {format(new Date(profile.created_at), "MMM dd, yyyy")}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleViewDetails(profile)}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedProfile(profile);
                                    setEditRole(profile.role);
                                    setEditOpen(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Change Role
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedProfile(profile);
                                    setNewPassword("");
                                    setConfirmPassword("");
                                    setResetPasswordOpen(true);
                                  }}
                                >
                                  <Key className="h-4 w-4 mr-2" />
                                  Reset Password
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => handleSuspendUser(profile.id)}
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Suspend
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Admins Tab */}
            <TabsContent value="admins">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading admins...</div>
              ) : (
                <div className="space-y-4">
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Admin accounts have full system access. Grant admin privileges carefully.
                    </AlertDescription>
                  </Alert>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Added</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {adminsList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No admins found
                        </TableCell>
                      </TableRow>
                    ) : adminsList.map((profile) => (
                          <TableRow key={profile.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4 text-orange-600" />
                                {profile.full_name || profile.username || "Admin"}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {profile.phone && (
                                  <div className="text-sm">{profile.phone}</div>
                                )}
                                {profile.whatsapp_number && (
                                  <div className="text-sm text-muted-foreground">
                                    WhatsApp: {profile.whatsapp_number}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {format(new Date(profile.created_at), "MMM dd, yyyy")}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleViewDetails(profile)}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedProfile(profile);
                                      setEditRole(profile.role);
                                      setEditOpen(true);
                                    }}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Change Role
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedProfile(profile);
                                      setNewPassword("");
                                      setConfirmPassword("");
                                      setResetPasswordOpen(true);
                                    }}
                                  >
                                    <Key className="h-4 w-4 mr-2" />
                                    Reset Password
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              Complete information about this user account
            </DialogDescription>
          </DialogHeader>
          {selectedProfile && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Name</p>
                  <p>{selectedProfile.full_name || selectedProfile.username || "N/A"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Role</p>
                  <Badge>{selectedProfile.role}</Badge>
                </div>
                
                {/* Contact Information Section */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Email</p>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {loadingEmail ? (
                          <span className="text-sm text-muted-foreground">Loading...</span>
                        ) : (
                          <span>{userEmail || "N/A"}</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Phone</p>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedProfile.phone || "N/A"}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">WhatsApp</p>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-green-600" />
                        <span>{selectedProfile.whatsapp_number || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {selectedProfile.business_name && (
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Store className="h-4 w-4" />
                      Business Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Business Name</p>
                        <p>{selectedProfile.business_name}</p>
                      </div>
                      {selectedProfile.business_category && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Category</p>
                          <p>{selectedProfile.business_category}</p>
                        </div>
                      )}
                      {selectedProfile.business_description && (
                        <div className="col-span-2">
                          <p className="text-sm font-medium text-muted-foreground">Description</p>
                          <p className="text-sm">{selectedProfile.business_description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedProfile.role === "seller" && (
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-semibold mb-3">Seller Stats</h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Verified</p>
                        <Badge variant={selectedProfile.verified ? "default" : "secondary"}>
                          {selectedProfile.verified ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Products</p>
                        <p className="font-semibold">{selectedProfile.product_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total Sales</p>
                        <p className="font-semibold">{selectedProfile.total_sales || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Subscription</p>
                        <Badge variant="outline">{selectedProfile.subscription_tier || "free"}</Badge>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="col-span-2 border-t pt-4 mt-2">
                  <p className="text-sm font-medium text-muted-foreground">Joined</p>
                  <p>{format(new Date(selectedProfile.created_at), "PPP")}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>
              Update user account settings and permissions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="role">User Role</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="seller">Seller</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {editRole === "seller" && (
              <>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="verified"
                    checked={editVerified}
                    onChange={(e) => setEditVerified(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="verified">Verified Seller</Label>
                </div>
                
                <div>
                  <Label htmlFor="subscription">Subscription Tier</Label>
                  <Select value={editSubscriptionTier} onValueChange={setEditSubscriptionTier}>
                    <SelectTrigger id="subscription">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            <Button onClick={handleUpdateProfile} className="w-full">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedProfile?.full_name || selectedProfile?.username || 'this user'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
              />
              <p className="text-xs text-muted-foreground">
                Minimum 6 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <Button 
              onClick={handleResetPassword} 
              className="w-full"
              disabled={resettingPassword || !newPassword || !confirmPassword}
            >
              {resettingPassword ? (
                <>
                  <AlertCircle className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}